'use client'

import type { ReactNode } from 'react'
import type { Address } from 'viem'

import { buttonVariants, CopyButton, Text } from '@repo/ui'
import { ArrowDiagonalUp } from '@repo/ui/icons'
import { abbreviateAddress } from '@repo/utils'

import {
  EntityHoverCard,
  EntityHoverCardContent,
  EntityHoverCardDivider,
  EntityHoverCardHeader,
  EntityHoverCardRow,
  EntityHoverCardRows,
  EntityHoverCardTrigger
} from './EntityHoverCard'
import { EtherscanLink } from './EtherscanLink'
import { TokenIcon, type TokenIconSource } from './TokenIcon'

interface TokenHoverCardProps {
  chainId: number
  token: TokenIconSource & {
    address: Address
    name?: string
    decimals?: number
  }
  children: ReactNode
}

export function TokenHoverCard({ chainId, token, children }: TokenHoverCardProps) {
  const abbreviated = abbreviateAddress(token.address)

  return (
    <EntityHoverCard>
      <EntityHoverCardTrigger asChild>
        <div
          tabIndex={0}
          aria-label={`Token details for ${token.symbol}`}
          className="hover:bg-muted data-[state=open]:bg-muted focus-visible:ring-ring/50 inline-flex w-fit max-w-full min-w-0 cursor-default items-center rounded-md px-2 py-1 transition-colors outline-none focus-visible:ring-[3px]"
        >
          {children}
        </div>
      </EntityHoverCardTrigger>
      <EntityHoverCardContent className="w-max max-w-[calc(100vw-2rem)] min-w-72">
        <EntityHoverCardHeader
          avatar={
            <TokenIcon
              address={token.address}
              symbol={token.symbol}
              logoUrl={token.logoUrl}
              size="md"
            />
          }
          title={token.symbol}
          subtitle={token.name}
          wrap
          actions={
            <EtherscanLink
              chainId={chainId}
              address={token.address}
              className={buttonVariants({ variant: 'ghost', size: 'iconSm' })}
              aria-label={`View ${token.symbol} on Etherscan`}
            >
              <ArrowDiagonalUp className="text-muted-foreground size-5" />
            </EtherscanLink>
          }
        />

        <EntityHoverCardDivider />

        <EntityHoverCardRows aria-label={`Token details for ${token.symbol}`}>
          <EntityHoverCardRow label="Address">
            <CopyButton
              value={token.address}
              variant="ghost"
              size="sm"
              className="text-foreground -mr-2.5 text-xs"
              successMessage="Copied address to clipboard"
              label={`Copy token address ${abbreviated}`}
            >
              {abbreviated}
            </CopyButton>
          </EntityHoverCardRow>

          {token.decimals !== undefined && (
            <EntityHoverCardRow label="Decimals">
              <Text size="xs">{token.decimals}</Text>
            </EntityHoverCardRow>
          )}
        </EntityHoverCardRows>
      </EntityHoverCardContent>
    </EntityHoverCard>
  )
}
